import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { agentsAPI, skillsAPI, channelsAPI, testAPI, tenantsAPI, capsulesAPI, formatApiError } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Bot, Save, Loader2, Zap, Settings, Wrench, FlaskConical, Radio, Plus, Trash2, FileText, Key, ChevronRight, Eye, EyeOff, Sparkles, Info, ChevronDown, ChevronUp, Microscope } from 'lucide-react';
import { toast } from 'sonner';
import HelpButton from '@/components/HelpButton';
import ConfirmActionDialog from '@/components/ConfirmActionDialog';
import EditorDeHerramientas from '@/components/EditorDeHerramientas';
import EnsayarAgente from '@/components/EnsayarAgente';
import AgentRuntimeTab from '@/components/AgentRuntimeTab';
import QuePuedeHacer from '@/components/QuePuedeHacer';
import Criterios from '@/components/Criterios';


// ── Inline contextual help ──
function AgentInlineHelp({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mb-3">
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-[11px] text-brand-primary hover:text-[#001F6D] transition-colors">
        <Info className="w-3.5 h-3.5" />
        <span className="font-medium">{open ? 'Ocultar ayuda' : 'Como funciona esta seccion?'}</span>
        {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="mt-2 p-4 bg-blue-50/60 border border-blue-200/60 rounded-sm text-xs text-zinc-700 leading-relaxed space-y-2 animate-in slide-in-from-top-1 duration-200">
          {children}
        </div>
      )}
    </div>
  );
}

// ── AI Generate Button Component ──
function AIGenerateButton({ contentType, agentName, agentType, skillName, onGenerated }) {
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState('');
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!desc.trim()) { toast.error('Escribe una descripcion breve'); return; }
    setGenerating(true);
    try {
      const res = await skillsAPI.generateContent({
        content_type: contentType,
        description: desc.trim(),
        agent_name: agentName || null,
        agent_type: agentType || null,
        skill_name: skillName || null,
      });
      onGenerated(res.data.content);
      toast.success('Contenido generado con AI');
      setOpen(false);
      setDesc('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error generando contenido');
    } finally { setGenerating(false); }
  };

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}
        data-testid={`ai-generate-${contentType}`}
        className="h-7 px-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50 hover:border-amber-400 gap-1">
        <Sparkles className="w-3 h-3" /> AI
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-1.5 animate-in slide-in-from-right-2 duration-200">
      <Input
        data-testid={`ai-desc-${contentType}`}
        value={desc} onChange={e => setDesc(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handleGenerate(); if (e.key === 'Escape') setOpen(false); }}
        placeholder="Describe brevemente que necesitas..."
        className="h-7 text-xs w-64 rounded-sm"
        autoFocus
      />
      <Button size="sm" onClick={handleGenerate} disabled={generating}
        data-testid={`ai-submit-${contentType}`}
        className="h-7 px-2 text-xs bg-amber-500 hover:bg-amber-600 text-white rounded-sm">
        {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
      </Button>
      <Button variant="ghost" size="sm" onClick={() => { setOpen(false); setDesc(''); }}
        className="h-7 w-7 p-0 text-zinc-400">x</Button>
    </div>
  );
}

// ── Skill Test Connection Button ──
function SkillTestButton({ skillId, configValues }) {
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  const handleTest = async () => {
    setTesting(true);
    setResult(null);
    try {
      const res = await skillsAPI.testConnection(skillId, configValues);
      setResult(res.data);
    } catch (err) {
      setResult({ success: false, error: err.response?.data?.detail || 'Error de conexion' });
    } finally { setTesting(false); }
  };

  return (
    <div className="pt-2 space-y-2">
      <Button data-testid={`test-connection-${skillId}`} onClick={handleTest} disabled={testing}
        variant="outline" size="sm" className="h-7 text-[10px] rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50 gap-1">
        {testing ? <><Loader2 className="w-3 h-3 animate-spin" /> Probando...</> : <><FlaskConical className="w-3 h-3" /> Probar Conexion</>}
      </Button>
      {result && (
        <div className={`p-2 rounded-sm text-[10px] ${result.success ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}
          data-testid={`test-result-${skillId}`}>
          {result.success ? (
            <span className="font-medium">{result.message}{result.items_returned != null ? ` (${result.items_returned} items)` : ''}</span>
          ) : (
            <div>
              <span className="font-medium">{result.error}</span>
              {result.hint && <p className="mt-0.5 text-red-500">{result.hint}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function AgentsPage() {
  const { user } = useAuth();
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [configs, setConfigs] = useState({});
  const [editContent, setEditContent] = useState('');
  const [editType, setEditType] = useState('soul');
  const [saving, setSaving] = useState(false);
  const [allSkills, setAllSkills] = useState([]);
  const [agentSkills, setAgentSkills] = useState([]);
  const [modelConfig, setModelConfig] = useState({ primary: {}, fallback: {} });
  const [activeTab, setActiveTab] = useState('configs');
  const [allChannels, setAllChannels] = useState([]);
  const [agentChannels, setAgentChannels] = useState([]);
  const [llmProviders, setLlmProviders] = useState([]);
  const [capsules, setCapsules] = useState([]);
  const [capsuleId, setCapsuleId] = useState('');

  // Skill editor state
  const [selectedSkill, setSelectedSkill] = useState(null);
  const [skillEditorContent, setSkillEditorContent] = useState('');
  const [skillConfigValues, setSkillConfigValues] = useState({});
  const [showSecrets, setShowSecrets] = useState({});
  const [showCreateSkill, setShowCreateSkill] = useState(false);
  const [newSkill, setNewSkill] = useState({ skill_id: '', name: '', description: '' });
  const [confirmState, setConfirmState] = useState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });

  // Coordinator test
  const [testMessage, setTestMessage] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const tenantId = user?.tenant_id;

  useEffect(() => {
    if (tenantId) {
      loadCapsules();
      loadAllChannels();
      loadTenantProviders();
    }
  }, [tenantId]);

  useEffect(() => {
    if (tenantId && capsuleId) {
      loadAgents();
      loadAllSkills();
    }
  }, [tenantId, capsuleId]);

  const loadCapsules = async () => {
    try {
      const res = await capsulesAPI.list(tenantId);
      const items = res.data || [];
      setCapsules(items);
      const preferred = user?.capsule_ids?.[0] || '';
      if (!items.find((c) => c.capsule_id === capsuleId)) {
        setCapsuleId(items[0]?.capsule_id || preferred || '');
      }
    } catch (err) {
      console.error(err);
      setCapsuleId(user?.capsule_ids?.[0] || '');
    }
  };

  // El punto de "operando" solo sirve si la vista vuelve a preguntar. Se refresca cada 5s
  // y unicamente con la pestaña visible: si nadie esta mirando, no cuesta nada.
  useEffect(() => {
    if (!tenantId || !capsuleId) return;
    const tick = () => { if (!document.hidden) loadAgents(); };
    const id = setInterval(tick, 5000);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', tick); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, capsuleId]);

  const loadAgents = async () => {
    try { const res = await agentsAPI.list(tenantId, capsuleId); setAgents(res.data || []); }
    catch (err) { console.error(err); }
  };

  const loadAllSkills = async () => {
    if (!capsuleId) {
      setAllSkills([]);
      return;
    }
    try {
      const res = await skillsAPI.list({ capsule_id: capsuleId });
      setAllSkills(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadAllChannels = async () => {
    try { const res = await channelsAPI.list(); setAllChannels(res.data || []); }
    catch (err) { console.error(err); }
  };

  const loadTenantProviders = async () => {
    try {
      const res = await tenantsAPI.listLLMProviders(tenantId);
      setLlmProviders(res.data || []);
    } catch (err) { console.error(err); }
  };

  /** Limpia provider_ref huérfanos (borrados del tenant) para que el <select> no quede pegado a un valor invisible. */
  const sanitizeModelConfigProviders = useCallback((cfg, providers) => {
    const known = new Set((providers || []).map((p) => p.provider_ref).filter(Boolean));
    const next = JSON.parse(JSON.stringify(cfg || { primary: {}, fallback: {} }));
    const cleared = [];
    for (const branch of ['primary', 'fallback', 'router']) {
      const ref = next[branch]?.provider_ref;
      if (!ref) continue;
      if (!known.has(ref)) {
        cleared.push(`${branch}:${ref}`);
        if (branch === 'primary') {
          next.primary = { ...next.primary, provider_ref: '', provider: '', model: next.primary?.model || '' };
        } else {
          next[branch] = { ...next[branch], provider_ref: '', provider: '' };
        }
      }
    }
    return { config: next, cleared };
  }, []);

  useEffect(() => {
    if (!llmProviders.length || !selectedAgent) return;
    setModelConfig((prev) => {
      const { config, cleared } = sanitizeModelConfigProviders(prev, llmProviders);
      if (cleared.length) {
        toast.warning(
          `Provider(s) ya no existen en el tenant (${cleared.join(', ')}). Volvé a elegir el provider Grok y guardá.`
        );
      }
      return cleared.length ? config : prev;
    });
  }, [llmProviders, selectedAgent?.agent_id, sanitizeModelConfigProviders]);


  const selectAgent = async (agent) => {
    setSelectedAgent(agent);
    setActiveTab('configs');
    setSelectedSkill(null);
    try {
      const [agentRes, configRes, skillRes, chRes] = await Promise.allSettled([
        agentsAPI.get(tenantId, capsuleId, agent.agent_id),
        agentsAPI.getConfigs(tenantId, capsuleId, agent.agent_id),
        skillsAPI.getAgentSkills(tenantId, capsuleId, agent.agent_id),
        channelsAPI.getAgentChannels(tenantId, capsuleId, agent.agent_id),
      ]);
      const fullAgent = agentRes.status === 'fulfilled' ? agentRes.value.data : agent;
      setSelectedAgent(fullAgent);
      const cfgs = configRes.status === 'fulfilled' ? (configRes.value.data || {}) : {};
      const skills = skillRes.status === 'fulfilled' ? (skillRes.value.data || []) : [];
      const channels = chRes.status === 'fulfilled' ? (chRes.value.data || []) : [];
      setConfigs(cfgs);
      setAgentSkills(skills);
      setAgentChannels(channels);
      const firstType = Object.keys(cfgs)[0] || 'soul';
      setEditType(firstType);
      setEditContent(cfgs[firstType]?.content || '');
      setModelConfig(fullAgent.model_config || fullAgent.llm_config || { primary: {}, fallback: {} });
      // Refrescar providers por si se creó/borró Grok en Config Capsula
      loadTenantProviders();
    } catch (err) {
      setConfigs({}); setEditContent(''); setAgentSkills([]); setAgentChannels([]);
    }
  };

  const handleAgentRuntimeUpdated = (updated) => {
    setSelectedAgent(updated);
    setAgents((prev) => prev.map((a) => (a.agent_id === updated.agent_id ? { ...a, ...updated } : a)));
  };

  const handleConfigTabChange = (type) => { setEditType(type); setEditContent(configs[type]?.content || ''); };

  const handleSaveConfig = async () => {
    if (!selectedAgent) return;
    setSaving(true);
    try {
      await agentsAPI.createConfig(tenantId, capsuleId, selectedAgent.agent_id, {
        config_type: editType, content: editContent, change_summary: `Updated ${editType}`,
      });
      const res = await agentsAPI.getConfigs(tenantId, capsuleId, selectedAgent.agent_id);
      setConfigs(res.data || {});
      toast.success(`${editType.toUpperCase()}.md guardado`);
    } catch (err) { toast.error(formatApiError(err) || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleSaveModel = async () => {
    if (!selectedAgent) return;
    setSaving(true);
    try {
      const { config: sanitized, cleared } = sanitizeModelConfigProviders(modelConfig, llmProviders);
      if (cleared.length) {
        setModelConfig(sanitized);
        toast.error(
          `No se puede guardar: provider(s) inexistente(s) (${cleared.join(', ')}). Elegí un provider de la lista y volvé a guardar.`
        );
        setSaving(false);
        return;
      }
      const payload = JSON.parse(JSON.stringify(sanitized || {}));
      for (const b of ['primary', 'fallback', 'router']) {
        const o = payload[b];
        if (!o || typeof o !== 'object') continue;
        // No enviar provider_ref vacío en fallback/router
        if (b !== 'primary' && !o.provider_ref) {
          delete o.provider_ref;
          delete o.provider;
        }
        for (const k of ['temperature', 'max_tokens']) {
          if (typeof o[k] === 'number' && Number.isNaN(o[k])) delete o[k];
        }
        if (!o.reasoning_effort) delete o.reasoning_effort;
        else o.reasoning_effort = String(o.reasoning_effort).toLowerCase();
      }
      if (!payload.primary?.provider_ref) {
        toast.error('Elegí un Provider Ref primario de la lista (el Grok del tenant).');
        setSaving(false);
        return;
      }
      await skillsAPI.updateAgentModel(tenantId, capsuleId, selectedAgent.agent_id, payload);
      toast.success('Modelo actualizado');
      loadAgents();
    } catch (err) { toast.error(formatApiError(err) || 'Error al guardar modelo'); }
    finally { setSaving(false); }
  };

  const handleSaveCriterios = async (criterios) => {
    if (!selectedAgent) return;
    setSaving(true);
    try {
      await agentsAPI.update(tenantId, capsuleId, selectedAgent.agent_id, { criterios });
      setSelectedAgent((prev) => (prev ? { ...prev, criterios } : prev));
      toast.success('Criterio del agente guardado');
      loadAgents();
    } catch (err) {
      // El backend rechaza con el motivo escrito —"un criterio sin texto no dice nada"— y
      // mostrarlo es la diferencia entre poder corregirlo y volver a intentar a ciegas.
      toast.error(formatApiError(err) || 'No se pudo guardar el criterio');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectSkill = (skill) => {
    setSelectedSkill(skill);
    setSkillEditorContent(skill.content || '');
    setSkillConfigValues(skill.config_values || {});
    setShowSecrets({});
  };

  const handleSaveSkillContent = async () => {
    if (!selectedSkill) return;
    setSaving(true);
    try {
      await skillsAPI.update(selectedSkill.skill_id, {
        content: skillEditorContent,
        config_values: skillConfigValues,
      });
      toast.success(`Skill "${selectedSkill.name}" guardada`);
      loadAllSkills();
    } catch (err) {
      toast.error(formatApiError(err) || 'Error al guardar skill');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateSkill = async () => {
    if (!newSkill.skill_id.trim() || !newSkill.name.trim()) {
      toast.error('ID y Nombre son requeridos'); return;
    }
    setSaving(true);
    try {
      // Una habilidad es **prosa**: cuando actuar y con que criterio. La plantilla vieja arrancaba
      // con una herramienta de ejemplo que declaraba `{{URL}}` y `{{TOKEN}}` con
      // `config_schema: []` — o sea que el formulario de credenciales que la propia pantalla
      // dibuja no se podia llenar nunca, y la skill nacia rota. Las acciones se declaran en una
      // conexion y se atan en Conexiones -> Capacidades y vinculos.
      const defaultContent = `---\nname: ${newSkill.name}\nversion: 1.0\ntriggers:\n  - Describir cuando se activa esta skill\n---\n# ${newSkill.name}\n\n## Cuando usar\n- En que situacion el agente tiene que seguir esto\n\n## Como actuar\n- El criterio, el cuidado, que hacer con el resultado\n`;
      await skillsAPI.create({
        skill_id: newSkill.skill_id.trim().toLowerCase().replace(/\s+/g, '-'),
        name: newSkill.name.trim(),
        description: newSkill.description.trim(),
        content: defaultContent,
        config_schema: [],
        config_values: {},
      });
      toast.success(`Skill "${newSkill.name}" creada`);
      setShowCreateSkill(false);
      setNewSkill({ skill_id: '', name: '', description: '' });
      loadAllSkills();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error creando skill'); }
    finally { setSaving(false); }
  };

  const handleDeleteSkill = async (skillId) => {
    setConfirmState({
      open: true,
      title: 'Eliminar skill',
      description: `Se eliminara la skill "${skillId}" del catalogo.`,
      confirmText: 'Eliminar',
      onConfirm: async () => {
        try {
          await skillsAPI.delete(skillId);
          toast.success(`Skill "${skillId}" eliminada`);
          if (selectedSkill?.skill_id === skillId) setSelectedSkill(null);
          loadAllSkills();
        } catch (err) { toast.error('Error eliminando skill'); }
      },
    });
  };

  const isSkillEnabled = (skillId) => agentSkills.some(s => s.skill_id === skillId && s.enabled);
  const isChannelBound = (channelId) => agentChannels.some(c => c.channel_id === channelId && c.enabled);

  const toggleAgentChannel = (channelId) => {
    setAgentChannels(prev => {
      const existing = prev.find(c => c.channel_id === channelId);
      if (existing) return prev.map(c => c.channel_id === channelId ? { ...c, enabled: !c.enabled } : c);
      return [...prev, { channel_id: channelId, enabled: true, is_default: false }];
    });
  };

  const handleSaveChannels = async () => {
    if (!selectedAgent) return;
    setSaving(true);
    try {
      await channelsAPI.updateAgentChannels(tenantId, capsuleId, selectedAgent.agent_id, { channels: agentChannels });
      toast.success('Canales del agente actualizados');
    } catch (err) { toast.error(formatApiError(err) || 'Error al guardar canales'); }
    finally { setSaving(false); }
  };

  const handleTestCoordinator = async (e) => {
    e.preventDefault();
    if (!testMessage.trim()) return;
    setTesting(true); setTestResult(null);
    try { const res = await testAPI.classify(testMessage); setTestResult(res.data); }
    catch (err) { toast.error('Error en clasificacion'); }
    finally { setTesting(false); }
  };

  // Create agent
  const [showCreateAgent, setShowCreateAgent] = useState(false);
  const [newAgentId, setNewAgentId] = useState('');
  const [newAgentRole, setNewAgentRole] = useState('specialist');

  const handleCreateAgent = async () => {
    if (!capsuleId) {
      toast.error('Crea una cápsula primero (menú Cápsulas)');
      return;
    }
    if (!newAgentId.trim()) { toast.error('Agent ID requerido'); return; }
    try {
      await agentsAPI.create(tenantId, capsuleId, {
        agent_id: newAgentId.trim().toLowerCase().replace(/\s+/g, '-'),
        name: newAgentId.trim(), type: newAgentRole,
      });
      toast.success(`Agente "${newAgentId}" creado`);
      setShowCreateAgent(false); setNewAgentId(''); loadAgents();
    } catch (err) { toast.error('Error creando agente'); }
  };

  const handleDeleteAgent = async (agentId) => {
    setConfirmState({
      open: true,
      title: 'Eliminar agente',
      description: `Se eliminara el agente "${agentId}" de la capsula activa.`,
      confirmText: 'Eliminar',
      onConfirm: async () => {
        try {
          await agentsAPI.delete(tenantId, capsuleId, agentId);
          toast.success(`Agente "${agentId}" eliminado`);
          if (selectedAgent?.agent_id === agentId) setSelectedAgent(null);
          loadAgents();
        } catch (err) { toast.error('Error eliminando agente'); }
      },
    });
  };

  const CONFIG_TYPES = ['soul', 'rules', 'memory'];

  return (
    <div className="flex h-full" data-testid="agents-page">
      {/* Agent list sidebar */}
      <div className="w-72 border-r border-brand-border bg-white flex flex-col">
        <div className="h-14 flex items-center justify-between px-4 border-b border-brand-border">
          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-700">Agentes</span>
          <div className="flex gap-1">
            <select
              value={capsuleId}
              onChange={(e) => { setSelectedAgent(null); setCapsuleId(e.target.value); }}
              className="border border-brand-border rounded-sm h-7 px-1.5 text-[10px] max-w-[110px]"
              title="Capsula activa"
            >
              {capsules.map((c) => (
                <option key={c.capsule_id} value={c.capsule_id}>{c.capsule_id}</option>
              ))}
              {!capsules.length && <option value={capsuleId}>{capsuleId}</option>}
            </select>
            <Button variant="ghost" size="sm" onClick={() => capsules.length && setShowCreateAgent(!showCreateAgent)}
              disabled={!capsules.length}
              className="h-7 w-7 p-0 text-brand-primary disabled:opacity-40" data-testid="create-agent-toggle"><Plus className="w-3.5 h-3.5" /></Button>
            <HelpButton section="agents" />
          </div>
        </div>
        {showCreateAgent && (
          <div className="p-3 border-b border-brand-border bg-blue-50/30 space-y-2">
            <Input data-testid="new-agent-id" className="rounded-sm text-xs h-8" placeholder="ID del agente"
              value={newAgentId} onChange={e => setNewAgentId(e.target.value)} />
            <select data-testid="new-agent-role" className="w-full border border-brand-border rounded-sm p-1.5 text-xs h-8"
              value={newAgentRole} onChange={e => setNewAgentRole(e.target.value)}>
              <option value="specialist">Especialista</option>
              <option value="coordinator">Coordinador</option>
              <option value="assistant">Asistente</option>
            </select>
            <Button data-testid="create-agent-button" onClick={handleCreateAgent}
              className="w-full bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-8">Crear Agente</Button>
          </div>
        )}
        <div className="flex-1 overflow-auto">
          {!capsules.length && (
            <p className="p-4 text-[11px] text-amber-700 bg-amber-50 border-b border-amber-100">
              No hay cápsulas. Crea una en <span className="font-semibold">Cápsulas</span> para configurar agentes y habilidades.
            </p>
          )}
          {agents.map(a => (
            <div key={a.agent_id} className={`flex items-center border-b border-brand-border transition-colors duration-150 ${
              selectedAgent?.agent_id === a.agent_id ? 'bg-brand-surface' : 'hover:bg-zinc-50'}`}>
              <button data-testid={`agent-item-${a.agent_id}`} onClick={() => selectAgent(a)} className="flex-1 text-left px-4 py-3">
                <div className="flex items-center gap-2">
                  <Bot className="w-4 h-4 text-zinc-400" />
                  <span className="text-sm font-medium text-[#09090B]">{a.name}</span>
                  {a.is_system && <Badge className="text-[9px] bg-zinc-800 text-white rounded-sm px-1 py-0">SISTEMA</Badge>}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline" className="text-[10px] rounded-sm">{a.type}</Badge>
                  <span
                    title={a.operando
                      ? `Operando ahora (${a.turnos_en_vuelo} ${a.turnos_en_vuelo === 1 ? 'turno' : 'turnos'})`
                      : a.status === 'active' ? 'Activo, sin turnos en curso' : 'Inactivo'}
                    className={`w-1.5 h-1.5 rounded-full ${
                      a.operando ? 'bg-brand-success animate-pulse'
                        : a.status === 'active' ? 'bg-zinc-300' : 'bg-transparent ring-1 ring-zinc-300'}`}
                  />
                  <span className="text-[10px] text-zinc-400">{(a.skills || []).filter(s => s.enabled).length} skills</span>
                </div>
              </button>
              {!a.is_system && (
                <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteAgent(a.agent_id); }}
                  className="h-7 w-7 p-0 mr-2 text-zinc-300 hover:text-red-500" data-testid={`delete-agent-${a.agent_id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          ))}
        </div>

        {/* Coordinator test */}
        <div className="border-t border-brand-border p-3">
          <div className="flex items-center gap-2 mb-2">
            <FlaskConical className="w-3 h-3 text-brand-primary" />
            <span className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-700">Test Coordinador</span>
          </div>
          <form onSubmit={handleTestCoordinator}>
            <textarea data-testid="coordinator-test-input" value={testMessage}
              onChange={e => setTestMessage(e.target.value)}
              placeholder="Escribe un mensaje para clasificar..."
              className="w-full border border-brand-border rounded-sm p-2 text-xs h-16 focus:ring-2 focus:ring-brand-primary focus:outline-none resize-none" />
            <Button type="submit" data-testid="coordinator-test-button" disabled={testing}
              className="w-full mt-1 bg-brand-primary text-white rounded-sm text-xs h-7">
              {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Clasificar'}
            </Button>
          </form>
          {testResult && (
            <div className="mt-2 p-2 bg-brand-surface rounded-sm text-[10px] space-y-1" data-testid="coordinator-test-result">
              <div className="flex justify-between"><span className="text-zinc-500">Intent:</span><span className="font-mono font-bold">{testResult.coordinator_decision?.detected_intent}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Prioridad:</span><span className="font-mono font-bold">{testResult.coordinator_decision?.assigned_priority}</span></div>
              <div className="flex justify-between"><span className="text-zinc-500">Agente:</span><span className="font-mono font-bold text-brand-primary">{testResult.coordinator_decision?.routed_to_agent}</span></div>
            </div>
          )}
        </div>
      </div>

      {/* Agent detail */}
      <div className="flex-1 overflow-auto">
        {!selectedAgent ? (
          <div className="flex items-center justify-center h-full text-zinc-400">
            <p className="text-sm">Selecciona un agente para configurar</p>
          </div>
        ) : (
          <div className="p-6" data-testid="agent-config-editor">
            <div className="mb-4">
              <h2 className="font-heading text-xl font-bold text-[#09090B]">{selectedAgent.name}</h2>
              <p className="text-xs text-zinc-500 mt-1">{selectedAgent.description}</p>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-brand-surface border border-brand-border rounded-sm mb-4">
                <TabsTrigger value="configs" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
                  <Settings className="w-3 h-3 mr-1" /> Configs (.md)
                </TabsTrigger>
                <TabsTrigger value="model" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
                  <Zap className="w-3 h-3 mr-1" /> Modelo LLM
                </TabsTrigger>
                <TabsTrigger value="puede" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary" data-testid="agent-tab-puede">
                  <Wrench className="w-3 h-3 mr-1" /> Que puede hacer
                </TabsTrigger>
                <TabsTrigger value="skills" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
                  <FileText className="w-3 h-3 mr-1" /> Catalogo de habilidades
                </TabsTrigger>
                <TabsTrigger value="runtime" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary" data-testid="agent-tab-runtime">
                  <Microscope className="w-3 h-3 mr-1" /> Runtime
                </TabsTrigger>
                <TabsTrigger value="channels" className="text-xs rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
                  <Radio className="w-3 h-3 mr-1" /> Canales
                </TabsTrigger>
              </TabsList>

              {/* ── Configs tab (SOUL, RULES, MEMORY only — no more SKILLS sub-tab) ── */}
              <TabsContent value="configs">
                <AgentInlineHelp>
                  <p className="font-semibold text-[#09090B]">Archivos .md del Agente</p>
                  <p>Cada agente se define con 3 archivos Markdown que el LLM lee en cada interaccion:</p>
                  <p>- <strong>SOUL.md</strong>: Identidad y personalidad. Nombre, idioma, tono, objetivo, que tipo de consultas atiende. Es el "quien soy".</p>
                  <p>- <strong>RULES.md</strong>: Reglas de comportamiento. Que puede hacer, que NO puede hacer, cuando escalar, limites. Es el "como actuo".</p>
                  <p>- <strong>MEMORY.md</strong>: Aprendizajes acumulados. Soluciones frecuentes, patrones conocidos. Se alimenta desde el Monitor HITL con "Inyectar Aprendizaje".</p>
                  <p className="font-semibold text-[#09090B] mt-2">Playbooks (fusionar en RULES):</p>
                  <p>- Ticket / artefactos: <code className="bg-zinc-100 px-0.5">documentacion/configOmnisys/ticket-chat-RULES-playbook.md</code> (ws_write en el mismo turno).</p>
                  <p>- Investigacion KB: <code className="bg-zinc-100 px-0.5">soporte-kb-investigation-RULES-playbook.md</code> + pestana Runtime (investigation iterative).</p>
                  <p>- Heartbeat: habilidad <code className="bg-zinc-100 px-0.5">heartbeat-followup</code> = playbook del tick (silencio <code className="bg-zinc-100 px-0.5">HEARTBEAT_OK</code>); Equipo → Heartbeat = cuándo. Off no apaga el motor. Ver <code className="bg-zinc-100 px-0.5">heartbeat-followup-RULES-playbook.md</code>.</p>
                  <p className="font-semibold text-[#09090B] mt-2">Relacion con Config Capsula:</p>
                  <p>- El <strong>RULES.md</strong> define CUANDO el agente escala, pero la <strong>Capsula (tab Escalamiento)</strong> define A DONDE va.</p>
                  <p>- El Coordinador usa los intents/prioridades de la <strong>Capsula (tab Intents)</strong> para clasificar, y las reglas de <strong>Routing</strong> para decidir a que agente enviar.</p>
                  <p className="mt-2 text-blue-600 font-medium">Usa el boton AI para generar contenido automaticamente describiendo brevemente lo que necesitas.</p>
                </AgentInlineHelp>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <Tabs value={editType} onValueChange={handleConfigTabChange}>
                      <TabsList className="bg-brand-surface border border-brand-border rounded-sm">
                        {CONFIG_TYPES.map(t => (
                          <TabsTrigger key={t} value={t} data-testid={`config-tab-${t}`}
                            className="text-xs font-mono uppercase tracking-wider rounded-sm data-[state=active]:bg-white data-[state=active]:text-brand-primary">
                            {t.toUpperCase()}.MD
                          </TabsTrigger>
                        ))}
                      </TabsList>
                    </Tabs>
                    <AIGenerateButton
                      contentType={editType}
                      agentName={selectedAgent.name}
                      agentType={selectedAgent.type}
                      onGenerated={(content) => setEditContent(content)}
                    />
                  </div>
                  <Button data-testid="save-config-button" onClick={handleSaveConfig} disabled={saving}
                    className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-sm">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Guardar
                  </Button>
                </div>
                <div className="border border-brand-border rounded-sm overflow-hidden">
                  <div className="bg-[#09090B] px-4 py-2">
                    <span className="text-xs font-mono text-zinc-400">{editType.toUpperCase()}.md {configs[editType]?.version && `v${configs[editType].version}`}</span>
                  </div>
                  <textarea data-testid={`config-editor-${editType}`} value={editContent} onChange={e => setEditContent(e.target.value)}
                    className="w-full h-80 bg-[#0A0A0A] text-zinc-200 p-4 font-mono text-sm leading-relaxed focus:outline-none resize-none"
                    placeholder={`# ${editType.toUpperCase()}\n\nEscribe la configuracion...`} />
                </div>
              </TabsContent>

              {/* ── Model config tab ── */}
              <TabsContent value="model">
                <AgentInlineHelp>
                  <p className="font-semibold text-[#09090B]">Modelo LLM</p>
                  <p>Cada agente usa un modelo de IA (LLM) para generar respuestas. Aqui configuras cual modelo usa.</p>
                  <p>- <strong>Provider</strong>: OpenAI, Anthropic, o Google.</p>
                  <p>- <strong>Modelo</strong>: Ej: <code className="bg-zinc-100 px-1 rounded">gpt-4o-mini</code>, <code className="bg-zinc-100 px-1 rounded">claude-3.5-sonnet</code>.</p>
                  <p>- <strong>Temperature</strong>: 0 = respuestas exactas/predecibles. 1+ = respuestas mas creativas. Para soporte tecnico recomendado: 0.3-0.5.</p>
                  <p>- <strong>Max Tokens</strong>: Largo maximo de la respuesta. 4096 es un buen default.</p>
                  <p>- <strong>Fallback</strong>: Si el modelo primario falla (ej: rate limit), usa este modelo alternativo.</p>
                  <p className="mt-2 text-blue-600 font-medium">Consejo: Agentes N1 pueden usar modelos mas economicos (gpt-4o-mini). Agentes N2/PM pueden usar modelos mas potentes (gpt-4o).</p>
                </AgentInlineHelp>
                <Card className="border border-brand-border rounded-sm">
                  <CardHeader className="pb-3 border-b border-brand-border">
                    <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em]">Modelo Primario</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 grid grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.1em]">Provider Ref</Label>
                      <select data-testid="model-primary-provider" value={
                        (llmProviders.some((p) => p.provider_ref === modelConfig.primary?.provider_ref)
                          ? modelConfig.primary?.provider_ref
                          : '') || ''
                      } onChange={e => {
                        const provider = llmProviders.find(p => p.provider_ref === e.target.value);
                        setModelConfig(p => ({
                          ...p,
                          primary: {
                            ...p.primary,
                            provider_ref: e.target.value,
                            provider: provider?.provider || '',
                            // Al cambiar provider, tomar el modelo del tenant (evita grok + gemini-flash heredado)
                            model: provider?.model || '',
                          },
                        }));
                      }}
                        className="w-full border border-brand-border rounded-sm p-2 text-sm h-10">
                        <option value="">Seleccionar provider</option>
                        {llmProviders.filter(p => p.enabled !== false).map(p => (
                          <option key={p.provider_ref} value={p.provider_ref}>
                            {p.provider} · {p.model || 'sin modelo'} ({p.provider_ref})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.1em]">Modelo</Label>
                      <Input data-testid="model-primary-model" value={modelConfig.primary?.model || ''} onChange={e => setModelConfig(p => ({ ...p, primary: { ...p.primary, model: e.target.value } }))} className="rounded-sm" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.1em]">Temperature</Label>
                      <Input data-testid="model-primary-temp" type="number" step="0.1" min="0" max="2" value={modelConfig.primary?.temperature || 0.7} onChange={e => setModelConfig(p => ({ ...p, primary: { ...p.primary, temperature: parseFloat(e.target.value) } }))} className="rounded-sm" />
                      <p className="text-[10px] text-zinc-500 mt-1 leading-snug">Controla la aleatoriedad de la generación del modelo.</p>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.1em]">Max Tokens</Label>
                      <Input data-testid="model-primary-tokens" type="number" value={modelConfig.primary?.max_tokens || 4096} onChange={e => setModelConfig(p => ({ ...p, primary: { ...p.primary, max_tokens: parseInt(e.target.value) } }))} className="rounded-sm" />
                      <p className="text-[10px] text-zinc-500 mt-1 leading-snug">Limita tokens de <span className="font-medium">salida</span> por cada respuesta del modelo. No controla el tamaño del prompt ni del historial (input).</p>
                    </div>
                    <div className="col-span-4">
                      <Label className="text-xs uppercase tracking-[0.1em]">Reasoning effort</Label>
                      <select
                        data-testid="model-primary-reasoning"
                        value={modelConfig.primary?.reasoning_effort || ''}
                        onChange={e => setModelConfig(p => ({
                          ...p,
                          primary: { ...p.primary, reasoning_effort: e.target.value || undefined },
                        }))}
                        className="w-full max-w-xs border border-brand-border rounded-sm p-2 text-sm h-10"
                      >
                        <option value="">Default del proveedor</option>
                        <option value="low">low</option>
                        <option value="medium">medium</option>
                        <option value="high">high</option>
                      </select>
                      <p className="text-[10px] text-zinc-500 mt-1 leading-snug">Solo modelos con reasoning (p. ej. Grok). Vacío = no se envía el parámetro.</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border border-brand-border rounded-sm mt-4">
                  <CardHeader className="pb-3 border-b border-brand-border">
                    <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em]">Modelo Fallback</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 grid grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.1em]">Provider Ref</Label>
                      <select data-testid="model-fallback-provider" value={
                        (llmProviders.some((p) => p.provider_ref === modelConfig.fallback?.provider_ref)
                          ? modelConfig.fallback?.provider_ref
                          : '') || ''
                      } onChange={e => {
                        const provider = llmProviders.find(p => p.provider_ref === e.target.value);
                        setModelConfig(p => ({
                          ...p,
                          fallback: {
                            ...p.fallback,
                            provider_ref: e.target.value,
                            provider: provider?.provider || '',
                            model: provider?.model || '',
                          },
                        }));
                      }}
                        className="w-full border border-brand-border rounded-sm p-2 text-sm h-10">
                        <option value="">Sin fallback</option>
                        {llmProviders.filter(p => p.enabled !== false).map(p => (
                          <option key={p.provider_ref} value={p.provider_ref}>
                            {p.provider} · {p.model || 'sin modelo'} ({p.provider_ref})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div><Label className="text-xs uppercase tracking-[0.1em]">Modelo</Label>
                      <Input data-testid="model-fallback-model" value={modelConfig.fallback?.model || ''} onChange={e => setModelConfig(p => ({ ...p, fallback: { ...p.fallback, model: e.target.value } }))} className="rounded-sm" /></div>
                    <div><Label className="text-xs uppercase tracking-[0.1em]">Temperature</Label>
                      <Input type="number" step="0.1" min="0" max="2" value={modelConfig.fallback?.temperature || 0.7} onChange={e => setModelConfig(p => ({ ...p, fallback: { ...p.fallback, temperature: parseFloat(e.target.value) } }))} className="rounded-sm" />
                      <p className="text-[10px] text-zinc-500 mt-1 leading-snug">Controla la aleatoriedad de la generación del modelo.</p>
                    </div>
                    <div><Label className="text-xs uppercase tracking-[0.1em]">Max Tokens</Label>
                      <Input type="number" value={modelConfig.fallback?.max_tokens || 4096} onChange={e => setModelConfig(p => ({ ...p, fallback: { ...p.fallback, max_tokens: parseInt(e.target.value) } }))} className="rounded-sm" />
                      <p className="text-[10px] text-zinc-500 mt-1 leading-snug">Limita tokens de <span className="font-medium">salida</span> por cada respuesta del modelo. No controla el tamaño del prompt ni del historial (input).</p>
                    </div>
                    <div className="col-span-4">
                      <Label className="text-xs uppercase tracking-[0.1em]">Reasoning effort</Label>
                      <select
                        data-testid="model-fallback-reasoning"
                        value={modelConfig.fallback?.reasoning_effort || ''}
                        onChange={e => setModelConfig(p => ({
                          ...p,
                          fallback: { ...p.fallback, reasoning_effort: e.target.value || undefined },
                        }))}
                        className="w-full max-w-xs border border-brand-border rounded-sm p-2 text-sm h-10"
                      >
                        <option value="">Default del proveedor</option>
                        <option value="low">low</option>
                        <option value="medium">medium</option>
                        <option value="high">high</option>
                      </select>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border border-brand-border rounded-sm mt-4">
                  <CardHeader className="pb-3 border-b border-brand-border">
                    <CardTitle className="text-xs font-semibold uppercase tracking-[0.1em]">Modelo Router</CardTitle>
                    <p className="text-xs text-zinc-500 mt-1 font-normal normal-case tracking-normal">
                      Clasificación de mensajes nuevos por el coordinador. Provider vacío = hereda credenciales del modelo primario.
                    </p>
                  </CardHeader>
                  <CardContent className="p-4 grid grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.1em]">Provider Ref</Label>
                      <select data-testid="model-router-provider" value={
                        (llmProviders.some((p) => p.provider_ref === modelConfig.router?.provider_ref)
                          ? modelConfig.router?.provider_ref
                          : '') || ''
                      } onChange={e => {
                        const provider = llmProviders.find(p => p.provider_ref === e.target.value);
                        setModelConfig(p => ({
                          ...p,
                          router: {
                            ...p.router,
                            provider_ref: e.target.value,
                            provider: provider?.provider || '',
                            model: provider?.model || '',
                          },
                        }));
                      }}
                        className="w-full border border-brand-border rounded-sm p-2 text-sm h-10">
                        <option value="">Heredar del primario</option>
                        {llmProviders.filter(p => p.enabled !== false).map(p => (
                          <option key={p.provider_ref} value={p.provider_ref}>
                            {p.provider} · {p.model || 'sin modelo'} ({p.provider_ref})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div><Label className="text-xs uppercase tracking-[0.1em]">Modelo</Label>
                      <Input data-testid="model-router-model" value={modelConfig.router?.model || ''} onChange={e => setModelConfig(p => ({ ...p, router: { ...p.router, model: e.target.value } }))} className="rounded-sm" placeholder="ej. gemini-2.0-flash-lite" /></div>
                    <div><Label className="text-xs uppercase tracking-[0.1em]">Temperature</Label>
                      <Input type="number" step="0.1" min="0" max="2" value={modelConfig.router?.temperature ?? 0.2} onChange={e => setModelConfig(p => ({ ...p, router: { ...p.router, temperature: parseFloat(e.target.value) } }))} className="rounded-sm" />
                      <p className="text-[10px] text-zinc-500 mt-1 leading-snug">Controla la aleatoriedad de la generación del modelo.</p>
                    </div>
                    <div><Label className="text-xs uppercase tracking-[0.1em]">Max Tokens</Label>
                      <Input type="number" value={modelConfig.router?.max_tokens || 500} onChange={e => setModelConfig(p => ({ ...p, router: { ...p.router, max_tokens: parseInt(e.target.value) } }))} className="rounded-sm" />
                      <p className="text-[10px] text-zinc-500 mt-1 leading-snug">Limita tokens de <span className="font-medium">salida</span> por cada respuesta del modelo. No controla el tamaño del prompt ni del historial (input).</p>
                    </div>
                    <div className="col-span-4">
                      <Label className="text-xs uppercase tracking-[0.1em]">Reasoning effort</Label>
                      <select
                        data-testid="model-router-reasoning"
                        value={modelConfig.router?.reasoning_effort || ''}
                        onChange={e => setModelConfig(p => ({
                          ...p,
                          router: { ...p.router, reasoning_effort: e.target.value || undefined },
                        }))}
                        className="w-full max-w-xs border border-brand-border rounded-sm p-2 text-sm h-10"
                      >
                        <option value="">Default del proveedor</option>
                        <option value="low">low</option>
                        <option value="medium">medium</option>
                        <option value="high">high</option>
                      </select>
                    </div>
                  </CardContent>
                </Card>
                <Button data-testid="save-model-button" onClick={handleSaveModel} disabled={saving}
                  className="mt-4 bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-sm">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Guardar Modelo
                </Button>
              </TabsContent>

              {/* ── Que puede hacer: el agente en acciones, no en documentos ──
                  Ninguna URL, ningun metodo, ninguna credencial: eso es del catalogo y de
                  Conexiones. Aca se contesta lo unico que trae quien viene a configurar un
                  agente — si puede registrar un caso, leer el codigo, escribirle a otro. */}
              <TabsContent value="puede" data-testid="agent-puede-tab">
                <QuePuedeHacer
                  tenantId={tenantId}
                  capsuleId={capsuleId}
                  agente={selectedAgent}
                  onGuardado={(skills) => { setAgentSkills(skills); loadAgents(); }}
                  botonAI={({ skillName, onGenerated }) => (
                    <AIGenerateButton
                      contentType="override"
                      agentName={selectedAgent?.name}
                      agentType={selectedAgent?.type}
                      skillName={skillName}
                      onGenerated={onGenerated}
                    />
                  )}
                />

                {/* El criterio propio de este agente. Va abajo de lo que puede hacer y arriba
                    del ensayo, que es el orden en que alguien lo piensa: que puede hacer, con
                    que criterio, y despues probarlo. */}
                <div className="mt-4 border-t border-brand-border pt-4">
                  <Criterios
                    criterios={selectedAgent?.criterios || []}
                    onGuardar={handleSaveCriterios}
                    guardando={saving}
                    alcance="agente"
                  />
                </div>

                {/* El ensayo, donde se responde la pregunta que deja abierta configurar:
                    "y esto funciona?". Antes habia que escribir un correo real y leer la
                    autopsia. */}
                <div className="mt-4">
                  <EnsayarAgente tenantId={tenantId} capsuleId={capsuleId} agente={selectedAgent} />
                </div>
              </TabsContent>

              {/* ── El catalogo de skills: el documento, que es del tenant ── */}
              <TabsContent value="skills">
                <AgentInlineHelp>
                  <p className="font-semibold text-[#09090B]">El documento de una habilidad</p>
                  <p>Una habilidad es <strong>prosa</strong>: cuando actuar y con que criterio. Este catalogo es <strong>del tenant</strong>: el documento que se edita aca lo comparten todas las capsulas, asi que editarlo desde <code className="bg-zinc-100 px-0.5">sistemas</code> lo cambia tambien para marketing.</p>
                  <p className="font-semibold text-[#09090B] mt-2">Que agente la usa NO se decide aca</p>
                  <p>Se decide en <strong>Que puede hacer</strong>, que es donde estan el interruptor y la instruccion propia de este agente. Aca solo figura cuales usa el agente abierto, porque saber quien depende del documento cambia con que cuidado se lo edita.</p>
                  <p>Y la <strong>capsula</strong> puede no dejar pasar una habilidad a sus agentes (Config Capsula → Habilidades): en ese caso no aparece en Que puede hacer, por mas que este prendida.</p>
                  <p className="font-semibold text-[#09090B] mt-2">Las acciones</p>
                  <p>Se declaran con el <strong>editor de herramientas</strong> de abajo, que las guarda tipadas: metodo, parametros y quien pone cada uno. La seccion <code className="bg-zinc-100 px-0.5">## Herramientas</code> del markdown es la forma vieja y se sigue leyendo, pero una clave mal escrita ahi se pierde en silencio y el fallo aparece en produccion.</p>
                  <p>Las <strong>credenciales no van en la habilidad</strong>: viven en la conexion del sistema externo (Organizacion → Conexiones), y el vinculo dice cual recurso usa esta capsula.</p>
                  <p className="font-semibold text-[#09090B] mt-2">heartbeat-followup</p>
                  <p>Es el <strong>playbook del tick</strong> Heartbeat (criterios de silencio <code className="bg-zinc-100 px-0.5">HEARTBEAT_OK</code>). No apaga el motor: Equipo → Heartbeat decide cuando; con la habilidad apagada el tick sigue con SOUL/RULES.</p>
                </AgentInlineHelp>
                <div className="grid grid-cols-12 gap-4">
                  {/* Left: skill catalog */}
                  <div className="col-span-4 space-y-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-600">Catalogo de Habilidades</span>
                      <Button variant="ghost" size="sm" onClick={() => setShowCreateSkill(!showCreateSkill)}
                        className="h-6 w-6 p-0 text-brand-primary" data-testid="create-skill-toggle">
                        <Plus className="w-3 h-3" />
                      </Button>
                    </div>

                    {showCreateSkill && (
                      <Card className="border-brand-primary/40 border rounded-sm">
                        <CardContent className="p-3 space-y-2">
                          <Input data-testid="new-skill-id" className="rounded-sm text-xs h-7" placeholder="skill-id (ej: jira-integration)"
                            value={newSkill.skill_id} onChange={e => setNewSkill(p => ({ ...p, skill_id: e.target.value }))} />
                          <Input data-testid="new-skill-name" className="rounded-sm text-xs h-7" placeholder="Nombre"
                            value={newSkill.name} onChange={e => setNewSkill(p => ({ ...p, name: e.target.value }))} />
                          <Input data-testid="new-skill-desc" className="rounded-sm text-xs h-7" placeholder="Descripcion breve"
                            value={newSkill.description} onChange={e => setNewSkill(p => ({ ...p, description: e.target.value }))} />
                          <div className="flex gap-1">
                            <Button data-testid="create-skill-button" onClick={handleCreateSkill} disabled={saving}
                              className="flex-1 bg-brand-primary text-white rounded-sm text-xs h-7">Crear</Button>
                            <Button variant="ghost" size="sm" onClick={() => setShowCreateSkill(false)}
                              className="rounded-sm text-xs h-7">Cancelar</Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {allSkills.map(skill => {
                      const enabled = isSkillEnabled(skill.skill_id);
                      const isSelected = selectedSkill?.skill_id === skill.skill_id;

                      return (
                        <div key={skill.skill_id}
                          className={`border rounded-sm transition-all duration-150 ${
                            isSelected ? 'border-brand-primary bg-blue-50/50 shadow-sm' :
                            enabled ? 'border-brand-primary/30 bg-blue-50/20' : 'border-brand-border'
                          }`}>
                          <div className="p-3">
                            <button onClick={() => handleSelectSkill(skill)}
                              data-testid={`skill-select-${skill.skill_id}`}
                              className="w-full text-left flex items-center gap-2">
                              <FileText className={`w-3.5 h-3.5 ${enabled ? 'text-brand-primary' : 'text-zinc-300'}`} />
                              <div>
                                <span className="text-xs font-medium text-[#09090B] block">{skill.name}</span>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  {/* Que este agente la tenga se decide en «Que puede hacer». Aca
                                      solo se dice, porque el documento es del tenant y saber
                                      quien lo usa cambia con que cuidado se lo edita. */}
                                  {enabled && (
                                    <Badge variant="secondary" className="text-[9px] rounded-sm px-1 py-0 h-4">
                                      la usa este agente
                                    </Badge>
                                  )}
                                  {skill.config_schema?.length > 0 && (
                                    <Key className="w-2.5 h-2.5 text-amber-500" />
                                  )}
                                </div>
                              </div>
                              <ChevronRight className={`w-3 h-3 ml-auto text-zinc-300 transition-transform ${isSelected ? 'rotate-90 text-brand-primary' : ''}`} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Right: skill editor */}
                  <div className="col-span-8">
                    {!selectedSkill ? (
                      <div className="flex items-center justify-center h-64 text-zinc-400 border border-dashed border-brand-border rounded-sm">
                        <div className="text-center">
                          <FileText className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                          <p className="text-xs">Selecciona una habilidad para ver/editar su definicion</p>
                          <p className="text-[10px] text-zinc-300 mt-1">Las habilidades son documentos Markdown que definen capacidades del agente</p>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-sm font-bold text-[#09090B]">{selectedSkill.name}</h3>
                            <p className="text-[10px] text-zinc-500">{selectedSkill.description}</p>
                          </div>
                          {/* Una skill de plataforma no es configuracion: su definicion viaja con
                              el producto. El backend rechaza editarla y borrarla, asi que la
                              pantalla no lo ofrece — ofrecerlo seria un boton que solo sabe fallar. */}
                          {selectedSkill.origen === 'plataforma' ? (
                            <span
                              data-testid="skill-de-plataforma"
                              className="text-[10px] text-zinc-500 border border-brand-border rounded-sm px-2 py-1"
                            >
                              Definicion del producto — solo lectura
                            </span>
                          ) : (
                            <div className="flex gap-1 items-center">
                              <AIGenerateButton
                                contentType="skill"
                                agentName={selectedAgent?.name}
                                agentType={selectedAgent?.type}
                                skillName={selectedSkill.name}
                                onGenerated={(content) => setSkillEditorContent(content)}
                              />
                              <Button data-testid="save-skill-content-button" onClick={handleSaveSkillContent} disabled={saving}
                                className="bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-xs h-7 px-3">
                                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Guardar Skill
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteSkill(selectedSkill.skill_id)}
                                className="h-7 w-7 p-0 text-zinc-400 hover:text-red-500" data-testid="delete-skill-button">
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Config values (secrets) */}
                        {selectedSkill.config_schema?.length > 0 && (
                          <Card className="border-amber-200 border rounded-sm bg-amber-50/30">
                            <CardHeader className="py-2 px-3 border-b border-amber-200">
                              <CardTitle className="text-[10px] font-semibold uppercase tracking-[0.1em] text-amber-700 flex items-center gap-1">
                                <Key className="w-3 h-3" /> Configuracion (Credenciales)
                              </CardTitle>
                            </CardHeader>
                            <CardContent className="p-3 space-y-2">
                              {selectedSkill.config_schema.map(field => (
                                <div key={field.key} className="flex items-center gap-2">
                                  <Label className="text-[10px] w-32 text-zinc-600 shrink-0">{field.label || field.key}</Label>
                                  <div className="flex-1 relative">
                                    <Input
                                      data-testid={`skill-config-${field.key}`}
                                      type={field.secret && !showSecrets[field.key] ? 'password' : 'text'}
                                      value={skillConfigValues[field.key] || ''}
                                      onChange={e => setSkillConfigValues(p => ({ ...p, [field.key]: e.target.value }))}
                                      className="rounded-sm text-xs h-7 pr-8 font-mono"
                                      placeholder={field.required ? 'Requerido' : 'Opcional'}
                                    />
                                    {field.secret && (
                                      <button onClick={() => setShowSecrets(p => ({ ...p, [field.key]: !p[field.key] }))}
                                        className="absolute right-2 top-1.5 text-zinc-400 hover:text-zinc-600">
                                        {showSecrets[field.key] ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                              <div className="flex flex-wrap gap-2 pt-2 items-center">
                                <SkillTestButton skillId={selectedSkill.skill_id} configValues={skillConfigValues} />
                              </div>
                            </CardContent>
                          </Card>
                        )}

                        {/* Markdown editor */}
                        <div className="border border-brand-border rounded-sm overflow-hidden">
                          <div className="bg-[#09090B] px-4 py-2 flex items-center justify-between">
                            <span className="text-xs font-mono text-zinc-400">{selectedSkill.skill_id}.md</span>
                            <span className="text-[10px] text-zinc-500">v{selectedSkill.version || '1.0'}</span>
                          </div>
                          <textarea
                            data-testid="skill-editor"
                            value={skillEditorContent}
                            onChange={e => setSkillEditorContent(e.target.value)}
                            readOnly={selectedSkill.origen === 'plataforma'}
                            className={`w-full h-96 bg-[#0A0A0A] p-4 font-mono text-xs leading-relaxed focus:outline-none resize-none ${
                              selectedSkill.origen === 'plataforma' ? 'text-zinc-500 cursor-default' : 'text-zinc-200'
                            }`}
                            placeholder="---\nname: Mi Habilidad\nversion: 1.0\ntriggers:\n  - Cuando...\n---\n# Instrucciones\n\n## Herramientas\n\n### mi_herramienta\nDescripcion.\n- method: GET\n- endpoint: {{URL}}/path"
                          />
                        </div>

                        {/* No se ofrece en una skill de plataforma: su definicion viaja con el
                            producto y el backend rechaza editarla. Ofrecerlo seria un editor
                            que solo sabe fallar. */}
                        {selectedSkill.origen !== 'plataforma' && (
                          <EditorDeHerramientas
                            skill={selectedSkill}
                            tenantId={tenantId}
                            capsuleId={capsuleId}
                            onGuardada={(tools) => setSelectedSkill({ ...selectedSkill, tools })}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="runtime" data-testid="agent-runtime-tab-content">
                <AgentRuntimeTab
                  tenantId={tenantId}
                  capsuleId={capsuleId}
                  agent={selectedAgent}
                  agentSkills={agentSkills}
                  onAgentUpdated={handleAgentRuntimeUpdated}
                />
              </TabsContent>

              {/* ── Channels tab ── */}
              <TabsContent value="channels" data-testid="agent-channels-tab">
                <div className="space-y-3">
                  <p className="text-xs text-zinc-500">
                    Este agente atiende los canales marcados. <strong>Un canal que ningún agente tiene marcado no se atiende:</strong> sus mensajes se descartan sin responder.
                  </p>
                  {allChannels.length === 0 ? (
                    <p className="text-xs text-zinc-400 text-center py-6">No hay canales registrados.</p>
                  ) : (
                    allChannels.map(ch => {
                      const bound = isChannelBound(ch.channel_id);
                      return (
                        <Card key={ch.channel_id} className={`border rounded-sm ${bound ? 'border-brand-primary/40 bg-blue-50/20' : 'border-brand-border'}`}>
                          <CardContent className="p-3 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${ch.status === 'connected' ? 'bg-green-500' : 'bg-zinc-300'}`} />
                              <div>
                                <span className="text-sm font-medium text-[#09090B]">{ch.name}</span>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <Badge variant="outline" className="text-[9px] rounded-sm">{ch.channel_type}</Badge>
                                  <span className={`text-[10px] ${ch.status === 'connected' ? 'text-green-500' : 'text-zinc-400'}`}>{ch.status}</span>
                                </div>
                              </div>
                            </div>
                            <Switch data-testid={`channel-toggle-${ch.channel_id}`} checked={bound}
                              onCheckedChange={() => toggleAgentChannel(ch.channel_id)} />
                          </CardContent>
                        </Card>
                      );
                    })
                  )}
                </div>
                {allChannels.length > 0 && (
                  <Button data-testid="save-agent-channels-button" onClick={handleSaveChannels} disabled={saving}
                    className="mt-4 bg-brand-primary hover:bg-[#001F6D] text-white rounded-sm text-sm">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Guardar Canales
                  </Button>
                )}
              </TabsContent>
            </Tabs>
          </div>
        )}
      </div>
      <ConfirmActionDialog
        open={confirmState.open}
        onOpenChange={(open) => {
          if (!open) setConfirmState({ open: false, title: '', description: '', onConfirm: null, confirmText: 'Confirmar' });
        }}
        title={confirmState.title}
        description={confirmState.description}
        confirmText={confirmState.confirmText}
        onConfirm={confirmState.onConfirm}
      />
    </div>
  );
}
